<template>
  <div class="page-container">
    <!-- 筛选区域 -->
    <Filter :params="params" @search="searchData">
      <template #handle>
        <el-form-item>
          <el-button type="danger" @click="handleAdd">
            + 新建机型
          </el-button>
        </el-form-item>
      </template>
    </Filter>

    <!-- 表格区域 -->
    <Table
      ref="tableRef"
      :tableData="tableDataList"
      :total="data.total"
      @update="updatePage"
      @sort-change="handleSortChange"
    >
      <!-- 产品ID -->
      <el-table-column prop="productId" label="产品ID" width="120" />

      <!-- 产品名称 -->
      <el-table-column prop="productName" label="产品名称" min-width="160" />

      <!-- 型号名称 -->
      <el-table-column prop="modelName" label="型号名称" min-width="160" />

      <!-- 型号版本 -->
      <el-table-column prop="modelVersion" label="型号版本" width="120" />

      <!-- 负载 -->
      <el-table-column prop="load" label="负载" width="100" sortable="custom">
        <template #default="{ row }">
          {{ row.load }} kg
        </template>
      </el-table-column>

      <!-- 自由度 -->
      <el-table-column prop="dof" label="自由度" width="100" sortable="custom" />

      <!-- 创建方式 -->
      <el-table-column prop="createType" label="创建方式" width="120">
        <template #default="{ row }">
          <el-tag
            size="small"
            effect="light"
            :type="getCreateTypeTag(row.createType)"
          >
            {{ row.createType }}
          </el-tag>
        </template>
      </el-table-column>

      <!-- 操作 -->
      <el-table-column label="操作" fixed="right" width="160">
        <template #default="{ row }">
          <el-button type="text" size="small" @click="handleView(row)">查看</el-button>
          <el-button type="text" size="small" @click="handleEdit(row)">编辑</el-button>
          <el-button type="text" size="small" style="color: red;" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </Table>

    <!-- 新增/编辑/查看弹窗 -->
    <el-dialog
      :title="dialogTitle"
      v-model="data.dialogVisible"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      width="600px"
      class="model-dialog"
    >
      <el-form
        :model="formData"
        label-width="110px"
        ref="formRef"
        class="model-form"
      >
        <!-- 产品ID（仅查看模式） -->
        <el-form-item label="产品ID" v-if="dialogMode === 'view'">
          <el-input v-model="formData.productId" disabled class="view-input" />
        </el-form-item>

        <!-- 产品名称 -->
        <el-form-item label="产品名称" :required="dialogMode !== 'view'">
          <el-input
            v-model="formData.productName"
            placeholder="如 JAKA Zu 7"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 型号名称 -->
        <el-form-item label="型号名称" :required="dialogMode !== 'view'">
          <el-input
            v-model="formData.modelName"
            placeholder="如 Zu 7"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 型号版本 -->
        <el-form-item label="型号版本" :required="dialogMode !== 'view'">
          <el-input
            v-model="formData.modelVersion"
            placeholder="如 V2.0"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 负载 -->
        <el-form-item label="负载 (kg)" :required="dialogMode !== 'view'">
          <el-input
            v-model="formData.load"
            type="number"
            placeholder="如 7"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 自由度 -->
        <el-form-item label="自由度" :required="dialogMode !== 'view'">
          <el-input
            v-model="formData.dof"
            type="number"
            placeholder="如 6"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 创建方式 -->
        <el-form-item label="创建方式">
          <el-select
            v-model="formData.createType"
            placeholder="请选择"
            style="width: 100%;"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          >
            <el-option label="手工" value="手工" />
            <el-option label="MES" value="MES" />
            <el-option label="系统自动" value="系统自动" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <template v-if="dialogMode === 'view'">
          <el-button size="default" @click="data.dialogVisible = false">关闭</el-button>
        </template>
        <template v-else>
          <el-button size="default" @click="data.dialogVisible = false">取消</el-button>
          <el-button type="danger" size="default" @click="handleSubmit">
            {{ dialogMode === 'edit' ? '保存' : '新建' }}
          </el-button>
        </template>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import Table from "@/components/table/index";
import Filter from "@/components/table/filter";
import { ref, reactive } from "vue";

export default {
  name: 'DeviceModelManagement',
  components: {
    Table,
    Filter,
  },
  setup() {
    // ===== 筛选参数配置 =====
    const params = [
      {
        key: 'productName',
        value: '',
        label: '产品名称',
        placeholder: '产品名称',
      },
      {
        key: 'modelName',
        value: '',
        label: '型号名称',
        placeholder: '型号名称',
      },
      {
        key: 'load',
        value: '',
        label: '负载',
        placeholder: '负载',
      },
    ];

    // ===== 数据状态 =====
    const data = reactive({
      page: 1,
      size: 10,
      total: 0,
      dialogVisible: false,
    });

    const tableDataList = ref([]);
    const formRef = ref(null);
    const dialogMode = ref('add'); // add | edit | view
    const dialogTitle = ref('新建机型');
    const editId = ref(null);
    const query = reactive({});

    // 表单数据结构
    const formData = reactive({
      productId: '',
      productName: '',
      modelName: '',
      modelVersion: '',
      load: '',
      dof: '',
      createType: '手工',
    });

    // ===== 模拟数据 (Mock Data) 对应截图 =====
    const mockData = [
      { id: 1, productId: 'P1001', productName: 'JAKA Zu 7',  modelName: 'Zu 7',  modelVersion: 'V2.0', load: 7,  dof: 6, createType: '手工' },
      { id: 2, productId: 'P1002', productName: 'JAKA Zu 12', modelName: 'Zu 12', modelVersion: 'V1.0', load: 12, dof: 6, createType: 'MES' },
      { id: 3, productId: 'P1003', productName: 'JAKA Pro 16', modelName: 'Pro 16', modelVersion: 'V2.0', load: 16, dof: 6, createType: 'MES' },
      { id: 4, productId: 'P1004', productName: 'JAKA Zu 20', modelName: 'Zu 20', modelVersion: 'V1.0', load: 20, dof: 6, createType: '系统自动' },
    ];

    // ===== 创建方式 tag 类型映射 =====
    const getCreateTypeTag = (type) => {
      if (type === '手工') return 'info';        // 灰色
      if (type === 'MES') return 'primary';      // 蓝色
      if (type === '系统自动') return 'success'; // 绿色/紫色（Element Plus 默认 success 是绿色）
      return 'info';
    };

    // ===== 获取数据 (Mock 前端过滤 + 分页) =====
    const getData = async () => {
      try {
        let filtered = mockData.filter(item => {
          const matchProductName = query.productName ? item.productName.includes(query.productName) : true;
          const matchModelName = query.modelName ? item.modelName.includes(query.modelName) : true;
          const matchLoad = query.load ? String(item.load).includes(String(query.load)) : true;
          return matchProductName && matchModelName && matchLoad;
        });

        data.total = filtered.length;
        const start = (data.page - 1) * data.size;
        const end = start + data.size;
        tableDataList.value = filtered.slice(start, end);
      } catch (error) {
        console.error('getDataFailed:', error);
        ElMessage.error('获取数据失败');
      }
    };

    // ===== 搜索 / 分页 / 排序 =====
    const searchData = (searchParams) => {
      Object.assign(query, searchParams);
      data.page = 1;
      getData();
    };

    const updatePage = (page, size) => {
      data.page = page || 1;
      data.size = size || 10;
      getData();
    };

    const handleSortChange = () => {
      getData();
    };

    // ===== 重置表单 =====
    const resetForm = () => {
      formData.productId = '';
      formData.productName = '';
      formData.modelName = '';
      formData.modelVersion = '';
      formData.load = '';
      formData.dof = '';
      formData.createType = '手工';
      editId.value = null;
    };

    // ===== 新增 =====
    const handleAdd = () => {
      dialogMode.value = 'add';
      dialogTitle.value = '新建机型';
      resetForm();
      data.dialogVisible = true;
    };

    // ===== 编辑 =====
    const handleEdit = (row) => {
      dialogMode.value = 'edit';
      dialogTitle.value = '编辑机型';
      editId.value = row.id;
      Object.assign(formData, {
        productId: row.productId,
        productName: row.productName,
        modelName: row.modelName,
        modelVersion: row.modelVersion,
        load: row.load,
        dof: row.dof,
        createType: row.createType,
      });
      data.dialogVisible = true;
    };

    // ===== 查看 =====
    const handleView = (row) => {
      dialogMode.value = 'view';
      dialogTitle.value = '查看机型';
      editId.value = row.id;
      Object.assign(formData, {
        productId: row.productId,
        productName: row.productName,
        modelName: row.modelName,
        modelVersion: row.modelVersion,
        load: row.load,
        dof: row.dof,
        createType: row.createType,
      });
      data.dialogVisible = true;
    };

    // ===== 提交 =====
    const handleSubmit = async () => {
      if (!formData.productName) {
        ElMessage.warning('请输入产品名称');
        return;
      }
      if (!formData.modelName) {
        ElMessage.warning('请输入型号名称');
        return;
      }
      if (!formData.modelVersion) {
        ElMessage.warning('请输入型号版本');
        return;
      }
      if (formData.load === '' || formData.load === null) {
        ElMessage.warning('请输入负载');
        return;
      }
      if (formData.dof === '' || formData.dof === null) {
        ElMessage.warning('请输入自由度');
        return;
      }

      ElMessage.success(dialogMode.value === 'edit' ? '编辑成功' : '新建成功');
      data.dialogVisible = false;
      getData();
    };

    // ===== 删除 =====
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除机型 "${row.modelName}" 吗？`,
          '删除确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          }
        );
        ElMessage.success('删除成功 (模拟)');
        getData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('deleteFailed:', error);
        }
      }
    };

    // ===== 初始化加载 =====
    getData();

    return {
      params,
      data,
      tableDataList,
      formRef,
      dialogMode,
      dialogTitle,
      formData,
      editId,
      getCreateTypeTag,
      searchData,
      updatePage,
      handleSortChange,
      handleAdd,
      handleEdit,
      handleView,
      handleSubmit,
      handleDelete,
    };
  },
};
</script>

<style lang="scss" scoped>
.page-container {
  height: 100%;
  padding: 50px 10px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* ===== 弹窗表单样式 ===== */
.model-form {
  padding-right: 10px;
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
}

/* 只读输入框：浅灰底、灰色文字 */
.view-input :deep(.el-input__wrapper),
.view-input :deep(.el-textarea__inner) {
  background-color: #f5f7fa;
  box-shadow: 0 0 0 1px #e4e7ed inset;
  color: #606266;
  -webkit-text-fill-color: #606266;
}
</style>